import Repository from "@/repository";

// Shared blob-metadata cache.
//
// Every thumbnail component used to resolve its own blob with a separate
// GET /upload/blob/{id} on mount, so a table with 25 rows of photos fired ~50
// XHRs in one burst against the browser's 6-connection-per-host cap. The ones
// that lost that race failed silently and their tile stayed blank forever.
//
// This module collects the ids requested within the same tick and resolves
// them in a single POST /upload/blob/getBlobs, de-duplicates concurrent and
// repeat requests for the same id, and caches the result for the session.

const BLOB_API = "/upload/blob";

// How long to collect ids before firing a batch. One animation frame is enough
// to catch a whole table's worth of thumbnails mounting together.
const BATCH_WINDOW_MS = 30;

// Keep a single request body sane; a very long list is split across batches.
const MAX_BATCH_SIZE = 100;

// A batch that fails on a transient error (network, queued request dropped) is
// retried once before the tiles are told to give up.
const MAX_ATTEMPTS = 2;
const RETRY_DELAY_MS = 400;

// id -> blob record, or null when the server has no such blob (negative cache,
// so a missing id renders a placeholder instead of retrying on every remount).
const cache = new Map();

// id -> in-flight promise, so N components asking for the same id share one
// request and all resolve together.
const inFlight = new Map();

// Deferreds waiting for the next flush: id -> {resolve, reject}.
let pending = new Map();
let flushTimer = null;

// Set false the first time getBlobs answers with 404/405, so a deployment
// whose server predates the batch route still resolves thumbnails one by one.
let batchSupported = true;

function scheduleFlush() {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(flush, BATCH_WINDOW_MS);
}

function flush() {
  flushTimer = null;
  if (pending.size === 0) return;

  const batch = pending;
  pending = new Map();

  const ids = Array.from(batch.keys());
  for (let i = 0; i < ids.length; i += MAX_BATCH_SIZE) {
    const slice = ids.slice(i, i + MAX_BATCH_SIZE);
    runBatch(slice, batch, 1);
  }
}

function runBatch(ids, batch, attempt) {
  const request = batchSupported
    ? Repository.post(`${BLOB_API}/getBlobs`, ids).then(payload => payload.data)
    : fetchIndividually(ids);

  request
    .then(records => settle(ids, batch, records))
    .catch(error => {
      const status = error.response && error.response.status;

      // The batch route is missing on this server — fall back permanently.
      if (batchSupported && (status === 404 || status === 405)) {
        batchSupported = false;
        console.warn(
          "blobCache: /upload/blob/getBlobs unavailable, falling back to single fetches"
        );
        runBatch(ids, batch, attempt);
        return;
      }

      // A 401 has already been turned into a logout by the repository
      // interceptor; retrying would just queue more doomed requests.
      if (attempt < MAX_ATTEMPTS && status !== 401) {
        setTimeout(() => runBatch(ids, batch, attempt + 1), RETRY_DELAY_MS);
        return;
      }

      console.warn(`blobCache: failed to resolve ${ids.length} blob(s)`, error);
      ids.forEach(id => {
        const deferred = batch.get(id);
        inFlight.delete(id);
        // Not cached: a later remount (or a retry by the user) may succeed.
        if (deferred) deferred.reject(error);
      });
    });
}

// Fallback path when the server has no batch endpoint. Resolves to the same
// shape as getBlobs — a list of records, missing ones simply absent.
function fetchIndividually(ids) {
  return Promise.all(
    ids.map(id =>
      Repository.get(`${BLOB_API}/${id}`)
        .then(payload => payload.data)
        .catch(() => null)
    )
  ).then(records => records.filter(record => record));
}

// Hand each waiting component its record. Ids the server did not return are
// resolved with null rather than left hanging.
function settle(ids, batch, records) {
  const byId = new Map();
  const list = Array.isArray(records) ? records : [];

  list.forEach((record, index) => {
    if (!record) return;
    // Positional fallback keeps this working if a record ever comes back
    // without an id, as long as the server preserves request order.
    const key = record.id || ids[index];
    if (key) byId.set(key, record);
  });

  ids.forEach(id => {
    const record = byId.has(id) ? byId.get(id) : null;
    cache.set(id, record);
    inFlight.delete(id);
    const deferred = batch.get(id);
    if (deferred) deferred.resolve(record);
  });
}

/**
 * Resolve one blob record by id. Returns a promise for the record, or for null
 * when the id is empty or the server has no such blob. Rejects only when the
 * lookup itself failed, so callers can tell "no image" from "load failed".
 */
export function getBlob(id) {
  if (!id) return Promise.resolve(null);

  if (cache.has(id)) return Promise.resolve(cache.get(id));

  const existing = inFlight.get(id);
  if (existing) return existing;

  const promise = new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
  });

  inFlight.set(id, promise);
  scheduleFlush();
  return promise;
}

/**
 * Seed the cache with records already fetched elsewhere (an upload response, a
 * list endpoint that embeds blobs) so their thumbnails never re-request.
 */
export function primeBlobs(records) {
  if (!Array.isArray(records)) return;
  records.forEach(record => {
    if (record && record.id) cache.set(record.id, record);
  });
}

/**
 * Media path for a blob, with the fallbacks that keep a tile from rendering a
 * 404. Blobs whose thumbnail was never generated fall back to the original
 * file; `preferOriginal` asks for the full-size file up front (large previews,
 * signatures, anything that would look soft upscaled from a thumbnail).
 */
export function mediaUrl(record, options) {
  if (!record || !record.account_id) return null;

  const preferOriginal = options && options.preferOriginal;
  const name =
    preferOriginal || !record.thumbnail ? record.file_name : record.thumbnail;

  if (!name) return null;
  return `/media/${record.account_id}/${name}`;
}

/**
 * Forget one blob so the next request re-reads it from the server. Call after
 * editing or deleting a blob, otherwise its tiles keep painting stale data.
 */
export function invalidateBlob(id) {
  if (!id) return;
  cache.delete(id);
  inFlight.delete(id);
}

/** Drop everything. Called on logout so the next account starts clean. */
export function clearBlobCache() {
  cache.clear();
  inFlight.clear();
  pending = new Map();
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  batchSupported = true;
}

export default { getBlob, primeBlobs, mediaUrl, invalidateBlob, clearBlobCache };
